import { isValidNumberInputKey, strToInteger } from '@/utils/numbers';
import { titleize } from '@/utils/strings';
import { decodeColors } from '@/utils/colors';
import { Select, Input, Label, Checkbox } from '@/common/components/FormElements';
import ColorPicker from '@/components/Editor/Form/fields/common/ColorPicker';
import RevealPill, { PillSwitch } from '@/components/Editor/Form/fields/RevealPill';
import { TemplateSection } from '@/components/Editor/Form/fields/Template';
import parentStyles from '@/features/WidgetEdit/Form/styles.module.scss';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MIN_ROTATION = 0;
const MAX_ROTATION = 360;
const ANCHORING_OPTIONS = ['center', 'start', 'end'];
const ALIGNMENT_OPTIONS = ['center', 'start', 'end', 'right', 'bottom', 'left', 'top'];

const EditorFieldsDataLabels = _ref => {
  let {
    nameSpace,
    display = false,
    exampleValue = '',
    color,
    anchoring: defaultAnchoring = 'end',
    alignment: defaultAlignment = 'center',
    rotation: defaultRotation = 0,
    template: defaultTemplate = '{{value}}',
    defaults = {},
    openButtonVariant,
    displayPercentages,
    hasPercentagesToggle,
    onChange
  } = _ref;
  const anchoringFieldId = `${nameSpace}-anchoring`;
  const alignmentFieldId = `${nameSpace}-alignment`;
  const rotationFieldId = `${nameSpace}-rotation`;
  const templateFieldId = `${nameSpace}-template`;
  const colorPickerFieldId = `${nameSpace}-color-picker`;
  const displayPercentegesId = `${nameSpace}-percentages`;
  return /*#__PURE__*/_jsxs(RevealPill, {
    label: "Data Labels",
    disableOpen: !display,
    openButtonVariant: openButtonVariant,
    activeState: /*#__PURE__*/_jsx(PillSwitch, {
      checked: display,
      onChange: checked => onChange({
        display: checked
      })
    }),
    children: [/*#__PURE__*/_jsx(TemplateSection, {
      id: templateFieldId,
      label: "Formatting",
      caption: "How data labels will be presented",
      exampleValue: exampleValue,
      template: defaultTemplate,
      forceDecimalFormattingOnly: displayPercentages,
      onChange: value => onChange({
        template: value
      })
    }), hasPercentagesToggle ? /*#__PURE__*/_jsx(Checkbox, {
      id: displayPercentegesId,
      className: parentStyles.formField,
      checked: displayPercentages ?? false,
      onChange: evt => onChange({
        displayPercentages: evt.target.checked
      }),
      children: /*#__PURE__*/_jsx("span", {
        children: "Show Percentages"
      })
    }) : null, /*#__PURE__*/_jsx(ColorPicker, {
      label: "Label Color",
      id: colorPickerFieldId,
      defaultValue: decodeColors(color ?? defaults.color).shift(),
      onChange: color => onChange({
        color: typeof color === 'string' ? color : null
      })
    }, colorPickerFieldId), /*#__PURE__*/_jsx(Label, {
      forInput: anchoringFieldId,
      text: "Anchoring",
      className: parentStyles.formField,
      children: /*#__PURE__*/_jsx(Select, {
        id: anchoringFieldId,
        defaultValue: defaults.anchoring ?? defaultAnchoring,
        onChange: evt => {
          onChange({
            anchoring: evt.currentTarget.value
          });
        },
        children: ANCHORING_OPTIONS.map(value => /*#__PURE__*/_jsx("option", {
          value: value,
          children: titleize(value)
        }, `${nameSpace}-${value}`))
      })
    }), /*#__PURE__*/_jsx(Label, {
      forInput: alignmentFieldId,
      text: "Alignment",
      className: parentStyles.formField,
      children: /*#__PURE__*/_jsx(Select, {
        id: alignmentFieldId,
        defaultValue: defaults.alignment ?? defaultAlignment,
        onChange: evt => {
          onChange({
            alignment: evt.currentTarget.value
          });
        },
        children: ALIGNMENT_OPTIONS.map(value => /*#__PURE__*/_jsx("option", {
          value: value,
          children: titleize(value)
        }, `${nameSpace}-${value}`))
      })
    }), /*#__PURE__*/_jsx(Label, {
      forInput: rotationFieldId,
      text: "Rotation",
      className: parentStyles.formField,
      children: /*#__PURE__*/_jsx(Input, {
        id: rotationFieldId,
        placeholder: "0",
        defaultValue: defaultRotation,
        type: "number",
        min: MIN_ROTATION,
        max: MAX_ROTATION,
        onKeyDown: e => {
          if (!isValidNumberInputKey(e.key)) {
            e.preventDefault();
          }
        },
        onChange: e => {
          const changed = strToInteger(e.target.value);

          if (changed < MIN_ROTATION || changed > MAX_ROTATION) {
            return;
          }

          onChange({
            rotation: changed
          });
        }
      })
    })]
  });
};

export default EditorFieldsDataLabels;